import type { EncodedAudioFrame } from '../mpegts/demuxerTypes';
import { BitReader } from '../aac/bitReader';
import { invariant } from '../util/invariant';
import { AacDecoder } from '../aac/aacDecoder';
import type { Fraction } from '../util/fractionType';
import { addFractions, floorFraction, frac, multiplyFractionBy } from '../util/integerMath';


export interface AdtsFrame {
  channelConfiguration: number;
  numberOfChannels: number;
  sampleRateInHz: number;
  frames: EncodedAudioFrame[];
  audioObjectType: number;
}


const frequencies = [
  96000,
  88200,
  64000,
  48000,
  44100,
  32000,
  24000,
  22050,
  16000,
  12000,
  11025,
  8000,
  7350,
];


export function parseAdtsFrame(packetPts: number, packetDts: number, buffer: Uint8Array): AdtsFrame | undefined {
  const frames: EncodedAudioFrame[] = [];
  let i = 0;

  let numberOfChannels: number | undefined;
  let sampleRateInHz: number | undefined;
  let audioObjectType: number | undefined;
  let channelConfiguration: number | undefined;
  let samplingFrequencyIndex: number | undefined;

  let currentPts : Fraction = frac(packetPts, 90000);
  let currentDts : Fraction = frac(packetDts, 90000);

  while (i + 7 < buffer.length) {
    // Decodes the ADTS header (see https://wiki.multimedia.cx/index.php?title=ADTS)
    const bitReader = new BitReader(buffer.subarray(i, i + 7));
    const syncWord = bitReader.readBits(12);
    invariant(syncWord === 0b1111_11111111, () => `Invalid sync word ${syncWord}`);
    bitReader.readBits(1); // MPEG Version
    const layer = bitReader.readBits(2); // Layer
    invariant(layer === 0, () => `Invalid layer ${layer}`);
    const protectionAbsence = bitReader.readBits(1);
    // protectionAbsence == 0 means that there is a 2 bytes CRC at the end of the header
    const dataStart = i + 7 + (protectionAbsence === 0 ? 2 : 0);

    audioObjectType = bitReader.readBits(2) + 1;
    samplingFrequencyIndex = bitReader.readBits(4);
    sampleRateInHz = frequencies[samplingFrequencyIndex];
    invariant(sampleRateInHz !== undefined, () => `Invalid samplingFrequencyIndex ${samplingFrequencyIndex}`);

    bitReader.readBits(1); // private bit

    const channelConfigurationIndex = bitReader.readBits(3);
    invariant(channelConfigurationIndex <= 8, () => `Invalid channelConfigurationIndex ${channelConfigurationIndex}`);
    channelConfiguration = channelConfigurationIndex;
    if (channelConfigurationIndex !== 0) {
      numberOfChannels = channelConfigurationIndex;
    }

    // Originality (1 bit)
    // Home (1 bit)
    // Copyright ID (2 bits)
    bitReader.readBits(4);

    const frameLength = bitReader.readBits(13);

    bitReader.readBits(11); // bit-reservoir per frame

    // An ADTS frame may contain more than one AAC frame
    const nAacFrames = 1 + bitReader.readBits(2);
    // There is also 1024 samples per channel in an AAC frame
    const sampleCount = nAacFrames * 1024;
    const adtsFrameDuration = frac(sampleCount, sampleRateInHz);

    // The frame length includes the length of the header,
    // so we need to adjust for that
    const dataEnd = i + frameLength;

    invariant(dataEnd <= buffer.length, 'Not enough data in buffer');

    frames.push({
      duration: floorFraction(multiplyFractionBy(adtsFrameDuration, 90000)),
      pts: floorFraction(multiplyFractionBy(currentPts, 90000)),
      dts: floorFraction(multiplyFractionBy(currentDts, 90000)),
      data: buffer.subarray(dataStart, dataEnd),
      // NOTE: the keyframe boolean makes sense for video, but I'm not sure
      //       what it is supposed to mean for AAC frames
      keyframe: true,
    });

    currentPts = addFractions(currentPts, adtsFrameDuration);
    currentDts = addFractions(currentDts, adtsFrameDuration);
    i += frameLength;
  }

  invariant(i === buffer.length, () => `Should have reached end of buffer ${buffer.length}, but stopped at ${i}`);

  // If channelConfigurationIndex was set to 0, it means that
  // the number of channels must be read from a PCE element inside
  // the AAC frame
  if (samplingFrequencyIndex !== undefined && channelConfiguration !== undefined && numberOfChannels === undefined && frames.length > 0) {
    numberOfChannels = new AacDecoder(samplingFrequencyIndex, channelConfiguration, false).decodeAacFrameFromArray(frames[0]!.data);
  }

  if (numberOfChannels === undefined || sampleRateInHz === undefined || audioObjectType === undefined || channelConfiguration === undefined) {
    return undefined;
  }

  return {
    channelConfiguration,
    audioObjectType,
    numberOfChannels,
    sampleRateInHz,
    frames,
  };
}
